<template>
  <div>
    <label
      v-if="label"
      :for="name"
      class="text-background-fg/70 mb-1 block text-xs font-medium"
    >
      {{ label }}
    </label>
    <div class="relative rounded-md">
      <div
        class="text-background-fg border-r-1 border-background-fg/8 absolute inset-y-0 left-0 flex w-10 items-center justify-center rounded-l-md"
      >
        <span class="font-semibold">
          {{ getCurrencySymbol(tenantCurrency) }}
        </span>
      </div>
      <input
        ref="inputRef"
        :disabled="disabled"
        type="text"
        :name="name"
        :class="[
          'border-1 border-background-fg/8 block w-full rounded-md pl-12 text-sm font-semibold focus:outline-none sm:text-sm',
          hasError
            ? 'border-error text-error placeholder-error focus:border-error focus:ring-error'
            : 'focus:ring-primary focus:border-primary',
        ]"
        :placeholder="placeholder"
        :value="disabled ? '' : formattedValue"
        :aria-invalid="hasError"
        enterkeyhint="done"
        @blur="handleBlur"
        @keydown.backspace="handleBackspace"
        @keydown.enter="handleEnter"
      />
    </div>
    <template v-if="hasError">
      <p class="text-error mt-1 text-xs font-medium">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useCurrency } from "@/composables/useCurrency"
import { useField } from "vee-validate"
import {
  useCurrencyInput,
  CurrencyDisplay,
  ValueScaling,
} from "vue-currency-input"

const props = withDefaults(
  defineProps<{
    name?: string
    label?: string
    placeholder?: string
    currency?: string
    disabled?: boolean
    min?: number
    max?: number
  }>(),
  {
    name: "",
    label: "",
    placeholder: "",
    currency: "GBP",
    disabled: false,
    min: undefined,
    max: undefined,
  }
)

const emit =
  defineEmits<
    (
      eventName: "update:modelValue",
      value: number | null
    ) => void
  >()

const {
  numberValue: internalValueNumber,
  inputRef,
  setValue: setInternalValue,
  formattedValue,
} = useCurrencyInput(
  {
    currency: props.currency,
    currencyDisplay: CurrencyDisplay.hidden,
    autoDecimalDigits: true,
    hideCurrencySymbolOnFocus: false,
    hideGroupingSeparatorOnFocus: false,
    hideNegligibleDecimalDigitsOnFocus: false,
    valueScaling: ValueScaling.precision,
    valueRange: {
      min: props.min,
      max: props.max,
    },
  },
  false
)

const {
  value: fieldValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField<number>(toRef(props, "name"))

const { getCurrencySymbol } = useCurrency()
const { currency: tenantCurrency } = useTenant()

watch(
  [fieldValue, inputRef],
  ([newFieldValue]) => {
    if (newFieldValue !== undefined) {
      setInternalValue(newFieldValue)
      emit("update:modelValue", newFieldValue)
    }
  },
  { immediate: true }
)

const hasError = computed(() => !!errorMessage.value)

watch(internalValueNumber, (newValue) => {
  if (newValue === null) {
    handleChange(0)
    return
  }

  handleChange(newValue)
})

const handleBackspace = (event: KeyboardEvent) => {
  if (internalValueNumber.value === 0) {
    event.preventDefault()
  }
}

const handleEnter = (event: Event) =>
  (event?.target as HTMLInputElement)?.blur()
</script>
